import { Stack } from "@mui/material"
import { TEditForm } from "../types"
import {
    Button,
    Chip,
    deleteStorageItem,
    DialogHandler,
    EEditorType,
    EnhancedFormModel,
    EStorageType,
    fetchRoute,
    FormsFactory,
    FullBar,
    GenericWrapperContext,
    getBrandUrl,
    Icon,
    InlineTextEdit,
    ListModel,
    ListsFactory,
    LoadingContainer,
    Modal,
    ResourceEdit,
    Typography,
} from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import { useContext, useState, useEffect } from "react"
import { ERoutes } from "../../../utils/url"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { useLocation, useParams } from "react-router-dom"
import BeeEditor from "../../../components/beeEditor"
import { logError } from "../../../utils/data"
import { ResourcePreview } from "../../../components/Resources/preview"
import "../styles.scss"
import { ResourceCopy } from "../../../components/Resources/copy"
import { ELogErrorMsg } from "../../../utils/data/types"
import { EEditorCacheKeys } from "../../../components/beeEditor/types"
import { buildDefaultUrls, buildFormEmbedded, buildFormScript, updateFormLayoutOptions } from "../utils"
import { AddTagsButton } from "../../../components/Tags"
import { FromCommonResourceEdit } from "../../forms/components/commonResource"
import ResourceDetailContainer from "../../../components/contentTemplate/resourceDetailContainer"


export default function FormDetails({ }: TEditForm) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const { formId } = useParams()

    const location = useLocation()

    const [isEditing, setIsEditing] = useState<boolean>(location.pathname.split('/').pop() === 'edit')
    const [loading, setLoading] = useState<boolean>(false)
    const [renderLoading, setRenderLoading] = useState<boolean>(false)

    const [form, setForm] = useState<EnhancedFormModel | undefined>()

    const [previewContent, setPreviewContent] = useState<string | undefined>(undefined)

    const [selectedList, setSelectedList] = useState<ListModel>()


    useEffect(() => {
        fetchForm().then((response) => {
            if (response instanceof EnhancedFormModel) {
                if (response?.list_id) {
                    ListsFactory.get({ id: response.list_id })
                        .then((list) => {
                            setSelectedList(list)
                        })
                }
            }
        })
    }, [])

    useEffect(() => {
        return () => {
            deleteStorageItem({ key: EEditorCacheKeys.listAttributes, type: EStorageType.session })
            deleteStorageItem({ key: EEditorCacheKeys.listInterests, type: EStorageType.session })
            deleteStorageItem({ key: EEditorCacheKeys.tags, type: EStorageType.session })
        }
    }, [])


    async function fetchForm() {
        setLoading(true)
        return FormsFactory.get({ id: formId || "" })
            .then(async (response) => {
                if (response instanceof EnhancedFormModel) {
                    const cForm = new EnhancedFormModel(response)
                    cForm.content.json = await updateFormLayoutOptions({
                        content: cForm.content.json,
                        listId: cForm.list_id,
                        submissionUrl: cForm.submission_url,
                        recaptchaSiteKey: cForm.recaptcha.site_key,
                        formId: cForm.id
                    })
                    setForm(cForm)
                    renderForm(cForm)
                    return cForm
                } else {
                    window.location.href = "#" + fetchRoute({ route: ERoutes.formEditBasic, params: { formId: formId } })
                    return response
                }
            }).catch((error) => {
                logError(ELogErrorMsg.ErrorFetchingForm, error)
                DialogHandler.show({
                    type: "warning",
                    title: t("general-error-msg"),
                })
                window.location.href = "#" + fetchRoute({ route: ERoutes.forms })
            }).finally(() => {
                setLoading(false)
            })
    }

    async function renderForm(cForm?: EnhancedFormModel) {
        setRenderLoading(true)
        const formToRender = cForm || form
        if (formToRender) {
            formToRender.render().then((content) => {
                setPreviewContent(content)
            }).catch((error) => {
                if (error?.code === 1507) {
                    setPreviewContent("")
                }
            }).finally(() => {
                setRenderLoading(false)
            })
        }
    }

    function handlePublish(e: React.MouseEvent<HTMLButtonElement>, btn: TButtonRef) {
        if (form) {
            form.publish().then((response) => {
                DialogHandler.show({
                    type: "success",
                    title: t("general-published"),
                })
                setForm(new EnhancedFormModel(response))
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action"),
                })
                logError(ELogErrorMsg.ErrorPublishingForm, error)
            }).finally(() => {
                btn.reset()
            })
        }
    }

    async function handleBrandingToggle(): Promise<any> {
        if (form) {
            return saveForm({ branding: form.branding === "smart" ? "basic" : "smart" }).then((data) => {
                if (data instanceof EnhancedFormModel) {
                    renderForm(data)
                    return data?.content?.json || null
                }
            })
        }
    }


    function handleInlineFieldConfirm({ name, value }: { name?: string, value?: string }) {
        if (form) {
            if (form.list_id === 0) {
                form[name as "name"] = value || ""
                setForm(new EnhancedFormModel(form))
            } else {
                saveForm({ [name as "name"]: value || "" })
            }
        }
    }

    function handleTag(tag: string, btn: TButtonRef) {
        if (form) {
            saveForm({ tags: [...(form.tags || []), tag] }).finally(() => {
                btn.reset()
            })
        }
    }

    function handleUntag(tag: string, btn: TButtonRef) {
        if (form) {
            saveForm({ tags: (form.tags || []).filter((t) => t !== tag) }).finally(() => {
                btn.reset()
            })
        }
    }


    async function handleOnEditorSave(jsonFile: string, htmlFile: string) {
        if (form) {
            return saveForm({ content: { json: JSON.parse(jsonFile) } }).then(() => {
                renderForm()
            })
        }
    }

    function handleOnClose() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.forms })
    }

    function handleFormEdit() {
        setIsEditing(true)
        window.location.href = "#" + fetchRoute({ route: ERoutes.formEdit, params: { formId: formId } })
    }

    function handleOnEditorClose() {
        setIsEditing(false)
        window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: formId } })
    }

    async function saveForm(dataToUpdate?: Partial<EnhancedFormModel | void>) {
        const formToSave = dataToUpdate || form
        if (formToSave) {
            if (formToSave.name == "") {
                return
            }

            const defaultUrls = buildDefaultUrls({ list: selectedList, account: genericWrapper.account, user: genericWrapper.user, form: form })

            if ((form?.post_redirect_url ?? "").trim() === "" || (formToSave?.post_redirect_url ?? "").trim() === "") {
                formToSave.post_redirect_url = defaultUrls.post_redirect_url
            }
            if (form?.double_opt_in_redirect_url === "" || formToSave?.double_opt_in_redirect_url === "") {
                formToSave.double_opt_in_redirect_url = defaultUrls.double_opt_in_redirect_url
            }

            setLoading(true)
            return form?.save(formToSave).then((response) => {
                setForm(new EnhancedFormModel(response))
                return response
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action"),
                })
                logError(ELogErrorMsg.ErrorSavingForm, error)
            }).finally(() => {
                setLoading(false)
            })
        }
    }

    const isPublished = form && form?.published_on > 0
    const hasUnpublishedChanges = form && ((form?.published_on > 0 && form?.updated_on - 1 > form?.published_on) || form?.published_on == null)

    return <Modal className={`editFormModal`}
        open
        heading={{
            logoUrl: getBrandUrl({ brand: genericWrapper.partnerBrand, elKey: "logo" }),
            titleContent: [
                <Typography key="formTitle" variant="title" color="body1.main">{t("general-form_one")}</Typography>,
                ...(form && [<Chip key="formChip" label={t(isPublished ? "general-published" : "general-unpublished")} color={isPublished ? "secondary" : "primary"} />] || [])
            ],
            actions: [
                <Button key="publish" animated disabled={!form || loading} color="secondary" variant='contained' startIcon={<Icon name={hasUnpublishedChanges ? "Publish18" : "Checkmark14"} />} onClick={handlePublish} >
                    {t(hasUnpublishedChanges ? "general-publish" : "general-published")}
                </Button>
            ],
            closeBtnText: t("general-close"),
            closeBtnOnClick: handleOnClose
        }}
        content={
            < LoadingContainer loading={!form}>
                {form && <ResourceDetailContainer>
                    <Stack gap={6}>
                        <FullBar
                            left={
                                <Typography variant="title" color="primary.main">
                                    <InlineTextEdit
                                        name="name"
                                        tooltipTitle={t("forms-details-name-tooltip")}
                                        onConfirm={handleInlineFieldConfirm}
                                        defaultValue={form.name}
                                        placeholder={t("general-untitled")}
                                    />
                                </Typography>

                            }
                            right={[
                                <Typography key="tagCount" variant="body1" className="semiBold">
                                    {form.tags?.length || 0} {t("general-tag", { count: form.tags?.length || 0 })}
                                </Typography>,
                                <AddTagsButton tagList={form.tags || []} onTag={handleTag} onUntag={handleUntag} />
                            ]}
                        />

                        <ResourceEdit
                            title={t("general-audience_one")}
                            desc={t("form-audience-desc")}
                            support={<Typography variant="body1">{t("form-audience-support")} <b>'{selectedList?.name || ""}'</b></Typography>}
                        />

                        <FromCommonResourceEdit form={form} saveForm={saveForm} />

                        {/*<ResourceEdit
                            title={t("general-link")}
                            desc={t("form-link-desc")}
                            support={form?.published_on > 0 ?
                                <ResourceCopy
                                    text={buildLink({ form, account: genericWrapper.account, user: genericWrapper.user })}
                                />

                                :
                                <Typography>{t("form-link-not-published")}</Typography>
                            }
                        />*/}

                        <ResourceEdit
                            title={t("general-embed-code")}
                            desc={t("form-embed-code-desc")}
                            support={form?.published_on > 0 ?
                                <ResourceCopy
                                    isCodeBlock
                                    text={buildFormEmbedded({ htmlContent: previewContent, form }) || "" /*buildFormScript({ form }) || ""*/} />
                                :
                                <Typography>{t("form-embed-snippet-not-published")}</Typography>
                            }
                        />

                        <ResourceEdit
                            title={t("general-embed-snippet")}
                            desc={t("form-embed-snippet-desc")}
                            support={form?.published_on > 0 ?
                                <ResourceCopy
                                    isCodeBlock
                                    text={buildFormScript({ form }) || ""} />
                                :
                                <Typography>{t("form-embed-snippet-not-published")}</Typography>
                            }
                        />

                        <ResourceEdit
                            title={t("form-submission-url")}
                            desc={t("form-submission-url-desc")}
                            support={<ResourceCopy text={form.submission_url} />}
                        />
                    </Stack>

                    <ResourcePreview
                        loading={renderLoading || loading}
                        content={previewContent}
                        actions={[
                            <Button
                                key="edit"
                                color="primary"
                                variant='contained'
                                size="small"
                                onClick={handleFormEdit} >
                                {t("general-edit-form")}
                            </Button>
                        ]}
                    />

                    <BeeEditor
                        hidden={!isEditing}
                        titleContent={<Typography variant="title" >{t("general-edit-form")}</Typography>}
                        onClose={handleOnEditorClose}
                        editorType={EEditorType.formDesigner}
                        initContent={form?.content?.json}
                        onSave={handleOnEditorSave}
                        onBrandingToggle={handleBrandingToggle}
                        contentContext={{
                            isSmart: form?.branding === "smart",
                            list: selectedList,
                            formAction: form?.submission_url,
                            recaptchaSiteKey: form?.recaptcha?.site_key
                        }}
                    />


                </ResourceDetailContainer>
                }
            </LoadingContainer >
        }
    />
}